import { type ReactNode, forwardRef, useRef } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { MoreVerticalIcon } from '../icons';
import { ROW_ACTIONS_MENU_WIDTH, ROW_ACTIONS_MENU_PROPS } from './constants';

export interface RowAction {
  readonly label: string;
  readonly icon?: ReactNode;
  readonly onClick?: () => void;
  readonly href?: string;
  readonly disabled?: boolean;
  readonly destructive?: boolean;
}

export interface DataTableRowActionsProps {
  readonly actions: readonly RowAction[];
  readonly className?: string;
}

export const DataTableRowActions = forwardRef<HTMLButtonElement, DataTableRowActionsProps>(
  function DataTableRowActions({ actions, className }, ref) {
    const buttonRef = useRef<HTMLButtonElement>(null);

    if (actions.length === 0) return null;

    return (
      <div className="flex justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              ref={(node) => {
                buttonRef.current = node;
                if (typeof ref === 'function') ref(node);
                else if (ref) ref.current = node;
              }}
              type="button"
              tabIndex={-1}
              data-row-actions-trigger
              // Don't let opening the menu bubble to a row-level onClick (e.g.
              // the preset-menu preview drawer). Radix's own toggle handler is
              // on this same element, so the menu still opens.
              onClick={(e) => e.stopPropagation()}
              className={cn(
                'group/trigger flex items-center justify-center h-6 w-6 rounded-xs cursor-pointer',
                'hover:bg-dinnerplate-white data-[state=open]:bg-dinnerplate-white',
                className
              )}
            >
              <MoreVerticalIcon
                className={cn(
                  'w-1 h-3',
                  'text-grey group-hover/trigger:text-off-black group-data-[state=open]/trigger:text-off-black'
                )}
              />
              <span className="sr-only">Open actions menu</span>
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            {...ROW_ACTIONS_MENU_PROPS}
            onCloseAutoFocus={(e) => {
              e.preventDefault();
              const row = buttonRef.current?.closest('tr[data-row-index]') as HTMLElement;
              row?.focus();
            }}
            style={{ width: ROW_ACTIONS_MENU_WIDTH }}
            className={cn(
              'bg-off-white border-thin border-dinnerplate-white rounded-lg',
              'p-3 shadow-soft'
            )}
          >
            {actions.map((action, index) => {
              const content = (
                <>
                  {action.icon && (
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      {action.icon}
                    </span>
                  )}
                  {action.label}
                </>
              );
              return (
                <DropdownMenuItem
                  key={index}
                  asChild={action.href !== undefined}
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick?.();
                  }}
                  disabled={action.disabled}
                  className={cn(
                    'flex items-center gap-1.5 px-1.5 py-1.5',
                    'rounded-md cursor-pointer',
                    'font-inter text-sm font-semibold',
                    'transition-colors',
                    action.destructive
                      ? ' text-chilli-red hover:bg-chilli-blush data-[highlighted]:bg-chilli-blush'
                      : ' hover:bg-dinnerplate-white-light data-[highlighted]:bg-dinnerplate-white-light'
                  )}
                >
                  {action.href !== undefined ? <a href={action.href}>{content}</a> : content}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
  );
});
